import type {
  ITransport,
  TransportAvailabilityInfo,
  TransportCloseInfo,
  TransportConnectionInfo,
} from './ITransport';

export const GATEWAY_SUBPROTOCOL = 'pardis.gateway.v2';
export const LEGACY_GATEWAY_SUBPROTOCOL = 'pardis.gateway.v1';

const CONNECT_TIMEOUT_MS = 10_000;
const HEARTBEAT_INTERVAL_MS = 15_000;
const HEARTBEAT_TIMEOUT_MS = 10_000;
const MAX_BUFFERED_BYTES = 512 * 1024;
const GATEWAY_CONTROL_TAG = 0xffff;
const GATEWAY_CONTROL_VERSION = 2;
const V2_HEADER_LENGTH = 8;

const enum GatewayOpcode {
  Ping = 0x01,
  Pong = 0x02,
  UpstreamSuspended = 0x10,
  UpstreamReady = 0x11,
}

type AttemptState = 'connecting' | 'open' | 'closing' | 'closed';

interface SocketAttempt {
  id: number;
  url: string;
  ws: WebSocket;
  state: AttemptState;
  localClose: boolean;
  terminalEmitted: boolean;
  connectSettled: boolean;
  connectPromise: Promise<TransportConnectionInfo>;
  resolveConnect: (info: TransportConnectionInfo) => void;
  rejectConnect: (error: Error) => void;
  connectTimer: ReturnType<typeof setTimeout> | null;
  heartbeatInterval: ReturnType<typeof setInterval> | null;
  heartbeatDeadline: ReturnType<typeof setTimeout> | null;
  heartbeatNonce: number | null;
  upstreamReady: boolean;
  upstreamEpoch: number;
}

/**
 * Browser WebSocket transport. Sends/receives binary frames (ArrayBuffer) that carry the game's native
 * [ushort MessageTag][payload] messages — the gateway relays them verbatim to the LiteNetLib game
 * server. WS-over-TCP gives reliable, ordered delivery, matching the game's ReliableOrdered channel.
 */
export class WebSocketTransport implements ITransport {
  private attempt: SocketAttempt | null = null;
  private nextConnectionId = 0;
  private nextHeartbeatNonce = 0;

  private openHandler: ((info: TransportConnectionInfo) => void) | null = null;
  private messageHandler: ((data: Uint8Array, connectionId: number) => void) | null = null;
  private closeHandler: ((info: TransportCloseInfo) => void) | null = null;
  private availabilityHandler: ((info: TransportAvailabilityInfo) => void) | null = null;
  private errorHandler: ((error: unknown) => void) | null = null;

  get isOpen(): boolean {
    return this.attempt?.state === 'open' && this.attempt.ws.readyState === WebSocket.OPEN;
  }

  get isReady(): boolean {
    return this.isOpen && (this.attempt?.upstreamReady ?? false);
  }

  get connectionId(): number | null {
    return this.attempt?.id ?? null;
  }

  get protocol(): string {
    return this.attempt?.ws.protocol ?? '';
  }

  connect(
    url: string,
    protocols: string[] = [GATEWAY_SUBPROTOCOL, LEGACY_GATEWAY_SUBPROTOCOL],
  ): Promise<TransportConnectionInfo> {
    const current = this.attempt;
    if (current?.url === url) {
      if (current.state === 'connecting') return current.connectPromise;
      if (current.state === 'open') {
        return Promise.resolve({ connectionId: current.id, protocol: current.ws.protocol });
      }
    }
    if (current) this.retire(current, 1000, 'connection_replaced', true);

    let ws: WebSocket;
    try {
      ws = new WebSocket(url, protocols);
    } catch (error) {
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }
    ws.binaryType = 'arraybuffer';

    let resolveConnect!: (info: TransportConnectionInfo) => void;
    let rejectConnect!: (error: Error) => void;
    const connectPromise = new Promise<TransportConnectionInfo>((resolve, reject) => {
      resolveConnect = resolve;
      rejectConnect = reject;
    });
    const attempt: SocketAttempt = {
      id: ++this.nextConnectionId,
      url,
      ws,
      state: 'connecting',
      localClose: false,
      terminalEmitted: false,
      connectSettled: false,
      connectPromise,
      resolveConnect,
      rejectConnect,
      connectTimer: null,
      heartbeatInterval: null,
      heartbeatDeadline: null,
      heartbeatNonce: null,
      upstreamReady: false,
      upstreamEpoch: 0,
    };
    this.attempt = attempt;

    attempt.connectTimer = setTimeout(() => {
      if (attempt.state !== 'connecting') return;
      this.errorHandler?.(new Error('WebSocket connection timed out'));
      this.retire(attempt, 1006, 'connect_timeout', false);
    }, CONNECT_TIMEOUT_MS);

    ws.onopen = () => {
      if (attempt.state !== 'connecting' || this.attempt !== attempt) {
        try { ws.close(1000, 'stale_connection'); } catch { /* best effort */ }
        return;
      }
      attempt.state = 'open';
      attempt.upstreamReady = ws.protocol !== GATEWAY_SUBPROTOCOL;
      this.clearConnectTimer(attempt);
      const info = { connectionId: attempt.id, protocol: ws.protocol };
      this.settleConnect(attempt, info);
      if (ws.protocol === GATEWAY_SUBPROTOCOL || ws.protocol === LEGACY_GATEWAY_SUBPROTOCOL) {
        this.startHeartbeat(attempt);
      }
      this.openHandler?.(info);
      if (attempt.upstreamReady) this.emitAvailability(attempt, true, false);
    };

    ws.onmessage = (event: MessageEvent) => {
      const deliver = (data: Uint8Array): void => {
        if (attempt.state !== 'open' || this.attempt !== attempt) return;
        if (this.consumeGatewayControl(attempt, data)) return;
        this.messageHandler?.(data, attempt.id);
      };
      if (event.data instanceof ArrayBuffer) {
        deliver(new Uint8Array(event.data));
      } else if (event.data instanceof Blob) {
        void event.data.arrayBuffer().then((buffer) => deliver(new Uint8Array(buffer))).catch((error) => {
          if (this.attempt === attempt) this.errorHandler?.(error);
        });
      } else {
        this.errorHandler?.(new Error('Gateway returned a non-binary WebSocket frame'));
        this.retire(attempt, 1003, 'binary_required', false);
      }
    };

    ws.onerror = (event: Event) => {
      if (attempt.state === 'closed') return;
      this.errorHandler?.(event);
      if (attempt.state === 'connecting') {
        this.rejectConnect(attempt, new Error('WebSocket error'));
      }
    };

    ws.onclose = (event: CloseEvent) => {
      this.finishAttempt(attempt, event.code || 1006, event.reason || 'websocket_closed');
    };

    return connectPromise;
  }

  send(data: Uint8Array): void {
    const attempt = this.attempt;
    if (!this.isReady || !attempt) {
      throw new Error(this.isOpen ? 'Game server is temporarily unavailable' : 'WebSocketTransport.send: socket is not open');
    }
    if (attempt.ws.bufferedAmount > MAX_BUFFERED_BYTES) {
      this.retire(attempt, 4003, 'client_backpressure', false);
      throw new Error('WebSocket send buffer exceeded the safety limit');
    }
    // Copy into a standalone ArrayBuffer (data may be a view over a larger/pooled buffer).
    attempt.ws.send(data.slice().buffer);
  }

  close(code = 1000, reason = 'client_closed', local = true): void {
    if (this.attempt) this.retire(this.attempt, code, reason, local);
  }

  probe(): void {
    const attempt = this.attempt;
    if (attempt?.state === 'open' &&
        (attempt.ws.protocol === GATEWAY_SUBPROTOCOL || attempt.ws.protocol === LEGACY_GATEWAY_SUBPROTOCOL)) {
      this.sendHeartbeat(attempt);
    }
  }

  onOpen(handler: (info: TransportConnectionInfo) => void): void {
    this.openHandler = handler;
  }

  onMessage(handler: (data: Uint8Array, connectionId: number) => void): void {
    this.messageHandler = handler;
  }

  onClose(handler: (info: TransportCloseInfo) => void): void {
    this.closeHandler = handler;
  }

  onAvailability(handler: (info: TransportAvailabilityInfo) => void): void {
    this.availabilityHandler = handler;
  }

  onError(handler: (error: unknown) => void): void {
    this.errorHandler = handler;
  }

  private retire(attempt: SocketAttempt, code: number, reason: string, local: boolean): void {
    if (attempt.state === 'closed') return;
    attempt.localClose = local;
    attempt.state = 'closing';
    this.clearTimers(attempt);
    this.rejectConnect(attempt, new Error(reason));
    try {
      if (attempt.ws.readyState === WebSocket.OPEN) attempt.ws.close(code, reason);
      else if (attempt.ws.readyState === WebSocket.CONNECTING) attempt.ws.close();
    } catch {
      // Browsers differ on close-during-handshake; the generation is retired regardless.
    }
    this.finishAttempt(attempt, code, reason);
  }

  private finishAttempt(attempt: SocketAttempt, code: number, reason: string): void {
    if (attempt.terminalEmitted) return;
    const current = this.attempt === attempt;
    attempt.terminalEmitted = true;
    attempt.state = 'closed';
    this.clearTimers(attempt);
    this.rejectConnect(attempt, new Error(reason));
    if (current) this.attempt = null;
    this.closeHandler?.({ code, reason, local: attempt.localClose, connectionId: attempt.id, current });
  }

  private settleConnect(attempt: SocketAttempt, info: TransportConnectionInfo): void {
    if (attempt.connectSettled) return;
    attempt.connectSettled = true;
    attempt.resolveConnect(info);
  }

  private rejectConnect(attempt: SocketAttempt, error: Error): void {
    if (attempt.connectSettled) return;
    attempt.connectSettled = true;
    attempt.rejectConnect(error);
  }

  private startHeartbeat(attempt: SocketAttempt): void {
    this.sendHeartbeat(attempt);
    attempt.heartbeatInterval = setInterval(() => this.sendHeartbeat(attempt), HEARTBEAT_INTERVAL_MS);
  }

  private sendHeartbeat(attempt: SocketAttempt): void {
    if (this.attempt !== attempt || attempt.state !== 'open') return;
    if (attempt.heartbeatNonce !== null) return;
    const nonce = ++this.nextHeartbeatNonce >>> 0;
    const isV2 = attempt.ws.protocol === GATEWAY_SUBPROTOCOL;
    const frame = new Uint8Array(isV2 ? V2_HEADER_LENGTH + 4 : 6);
    const view = new DataView(frame.buffer);
    view.setUint16(0, GATEWAY_CONTROL_TAG, true);
    if (isV2) {
      view.setUint8(2, GATEWAY_CONTROL_VERSION);
      view.setUint8(3, GatewayOpcode.Ping);
      view.setUint32(4, attempt.upstreamEpoch, true);
      view.setUint32(V2_HEADER_LENGTH, nonce, true);
    } else {
      view.setUint32(2, nonce, true);
    }
    try {
      if (attempt.ws.bufferedAmount > MAX_BUFFERED_BYTES) {
        this.retire(attempt, 4003, 'client_backpressure', false);
        return;
      }
      attempt.ws.send(frame.buffer);
      attempt.heartbeatNonce = nonce;
      attempt.heartbeatDeadline = setTimeout(() => {
        if (attempt.heartbeatNonce === nonce) this.retire(attempt, 4002, 'heartbeat_timeout', false);
      }, HEARTBEAT_TIMEOUT_MS);
    } catch (error) {
      this.errorHandler?.(error);
      this.retire(attempt, 4002, 'heartbeat_send_failed', false);
    }
  }

  private consumeGatewayControl(attempt: SocketAttempt, data: Uint8Array): boolean {
    if (data.length < 2 || new DataView(data.buffer, data.byteOffset, data.byteLength).getUint16(0, true) !== GATEWAY_CONTROL_TAG) {
      return false;
    }
    if (attempt.ws.protocol === GATEWAY_SUBPROTOCOL) {
      return this.consumeV2Control(attempt, data);
    }
    if (attempt.ws.protocol !== LEGACY_GATEWAY_SUBPROTOCOL || data.length !== 6) {
      this.retire(attempt, 1002, 'invalid_gateway_control', false);
      return true;
    }
    const nonce = new DataView(data.buffer, data.byteOffset, data.byteLength).getUint32(2, true);
    if (nonce === attempt.heartbeatNonce) {
      attempt.heartbeatNonce = null;
      if (attempt.heartbeatDeadline) clearTimeout(attempt.heartbeatDeadline);
      attempt.heartbeatDeadline = null;
    }
    return true;
  }

  private consumeV2Control(attempt: SocketAttempt, data: Uint8Array): boolean {
    if (data.length < V2_HEADER_LENGTH) {
      this.retire(attempt, 1002, 'invalid_gateway_control', false);
      return true;
    }
    const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    if (view.getUint8(2) !== GATEWAY_CONTROL_VERSION) {
      this.retire(attempt, 1002, 'invalid_gateway_control', false);
      return true;
    }

    const opcode = view.getUint8(3) as GatewayOpcode;
    const epoch = view.getUint32(4, true);
    if (epoch < attempt.upstreamEpoch) return true;
    attempt.upstreamEpoch = epoch;

    switch (opcode) {
      case GatewayOpcode.Pong: {
        if (data.length !== V2_HEADER_LENGTH + 4) break;
        const nonce = view.getUint32(V2_HEADER_LENGTH, true);
        if (nonce === attempt.heartbeatNonce) {
          attempt.heartbeatNonce = null;
          if (attempt.heartbeatDeadline) clearTimeout(attempt.heartbeatDeadline);
          attempt.heartbeatDeadline = null;
        }
        return true;
      }
      case GatewayOpcode.UpstreamSuspended:
        if (data.length !== V2_HEADER_LENGTH + 5) break;
        attempt.upstreamReady = false;
        this.availabilityHandler?.({
          state: 'suspended',
          connectionId: attempt.id,
          upstreamEpoch: epoch,
          resumeRequired: false,
          reason: view.getUint8(V2_HEADER_LENGTH),
          retryAfterMs: view.getUint32(V2_HEADER_LENGTH + 1, true),
        });
        return true;
      case GatewayOpcode.UpstreamReady:
        if (data.length !== V2_HEADER_LENGTH + 1) break;
        attempt.upstreamReady = true;
        this.emitAvailability(attempt, true, view.getUint8(V2_HEADER_LENGTH) === 1);
        return true;
    }

    this.retire(attempt, 1002, 'invalid_gateway_control', false);
    return true;
  }

  private emitAvailability(attempt: SocketAttempt, ready: boolean, resumeRequired: boolean): void {
    this.availabilityHandler?.({
      state: ready ? 'ready' : 'suspended',
      connectionId: attempt.id,
      upstreamEpoch: attempt.upstreamEpoch,
      resumeRequired,
    });
  }

  private clearConnectTimer(attempt: SocketAttempt): void {
    if (attempt.connectTimer) clearTimeout(attempt.connectTimer);
    attempt.connectTimer = null;
  }

  private clearTimers(attempt: SocketAttempt): void {
    this.clearConnectTimer(attempt);
    if (attempt.heartbeatInterval) clearInterval(attempt.heartbeatInterval);
    if (attempt.heartbeatDeadline) clearTimeout(attempt.heartbeatDeadline);
    attempt.heartbeatInterval = null;
    attempt.heartbeatDeadline = null;
    attempt.heartbeatNonce = null;
  }
}
